// Kiosk live transport — one HTTP boundary for config, presence code and punch.
// Production always uses ADR-020 KioskDevice authentication. The mock factory
// remains an explicit test/dev injection and is never selected implicitly.

import {
  ALL_CLASSIFICATIONS,
  type Classification,
  type EnvelopeMeta,
  type ErrorEntry,
} from "@novemai/error-envelope";

import { resolveKioskApiBaseUrl } from "../../shared/base-url.mjs";
import { readProvisionedDeviceToken } from "./device-token";

export {
  resolveKioskApiBaseUrl,
  type KioskApiBaseUrlEnvironment,
} from "../../shared/base-url.mjs";

export type PunchDirection = "in" | "out";

export interface KioskPunchRequest {
  /** ADR-020 server-issued employee PIN: exactly six decimal digits. */
  readonly pin: string;
  readonly direction: PunchDirection;
  readonly timezone: string;
}

export interface KioskPunchResult {
  readonly action: "clock_in" | "clock_out";
  readonly at: string;
  readonly employeeName: string | null;
}

export interface KioskConfig {
  readonly device_id: string;
  readonly location_label: string;
  readonly locale: "en-US" | "tr-TR";
}

export interface KioskPresenceCode {
  readonly presence_code: string;
  readonly device_id: string;
  readonly expires_at: string;
}

export type KioskPresenceCodeFetcher = () => Promise<KioskPresenceCode>;

interface KioskPunchErrorMetadata {
  readonly retryable?: boolean | undefined;
  readonly retryAfterMs?: number | undefined;
  readonly traceId?: string | undefined;
  /** The client cannot prove the punch did not commit, so retry must replay it. */
  readonly ambiguous?: boolean | undefined;
}

export class KioskPunchError extends Error {
  readonly messageKey: string;
  readonly classification: Classification | undefined;
  /** Canonical error-envelope retry guidance, when a response reached the API. */
  readonly retryable: boolean | undefined;
  readonly retryAfterMs: number | undefined;
  /** Safe-to-display support reference from envelope.meta.trace_id. */
  readonly traceId: string | undefined;
  /** True only when a retry must retain the exact durable request intent. */
  readonly ambiguous: boolean;

  constructor(
    messageKey: string,
    message?: string,
    classification?: Classification,
    metadata: KioskPunchErrorMetadata = {},
  ) {
    super(message ?? messageKey);
    this.name = "KioskPunchError";
    this.messageKey = messageKey;
    this.classification = classification;
    this.retryable = metadata.retryable;
    this.retryAfterMs = metadata.retryAfterMs;
    this.traceId = metadata.traceId;
    this.ambiguous = metadata.ambiguous === true;
    Object.setPrototypeOf(this, KioskPunchError.prototype);
  }
}

export interface PunchTransport {
  readonly kind: "mock" | "http";
  punch(request: KioskPunchRequest): Promise<KioskPunchResult>;
  /** Abandon an ambiguous in-memory request intent when the user goes home. */
  cancelPendingIntent?(): void;
}

export const PIN_PATTERN = /^\d{6}$/;

// ---------------------------------------------------------------------------
// Explicit mock transport — tests/dev injection only
// ---------------------------------------------------------------------------

const MOCK_PIN_DIRECTORY: ReadonlyMap<string, string> = new Map([
  ["104200", "Deniz Aydın"],
  ["110800", "Selin Korkmaz"],
  ["123300", "Emre Şahin"],
  ["131700", "Burcu Yılmaz"],
  ["140200", "Kaan Demir"],
]);

export interface MockPunchTransportOptions {
  readonly delayMs?: number;
}

export function createMockPunchTransport(
  options: MockPunchTransportOptions = {},
): PunchTransport {
  const delayMs = options.delayMs ?? 350;
  return {
    kind: "mock",
    async punch(request: KioskPunchRequest): Promise<KioskPunchResult> {
      if (delayMs > 0) {
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }
      const employeeName = PIN_PATTERN.test(request.pin)
        ? MOCK_PIN_DIRECTORY.get(request.pin)
        : undefined;
      if (employeeName === undefined) {
        throw new KioskPunchError(
          "errors.code.kiosk.pin_invalid",
          undefined,
          "validation",
        );
      }
      return {
        action: request.direction === "in" ? "clock_in" : "clock_out",
        at: new Date().toISOString(),
        employeeName,
      };
    },
  };
}

// ---------------------------------------------------------------------------
// HTTP transport
// ---------------------------------------------------------------------------

interface HttpPunchData {
  readonly entry: {
    readonly start_at: string;
    readonly end_at: string | null;
  };
  readonly action: "clock_in" | "clock_out";
  readonly employee_name: string | null;
}

interface HttpEnvelope<TData> {
  readonly data?: TData;
  readonly meta?: Pick<EnvelopeMeta, "trace_id">;
  /** Untrusted JSON until each item passes the spec-58 runtime boundary. */
  readonly errors?: readonly unknown[];
}

const KIOSK_LOCALES = new Set<KioskConfig["locale"]>(["en-US", "tr-TR"]);
const ISO_UTC_TIMESTAMP_RE =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;
const ERROR_CODE_RE = /^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)+$/;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isNonBlankString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function isErrorEnvelopeEntry(value: unknown): value is ErrorEntry {
  if (
    !isRecord(value) ||
    typeof value.code !== "string" ||
    !ERROR_CODE_RE.test(value.code) ||
    typeof value.classification !== "string" ||
    !ALL_CLASSIFICATIONS.includes(value.classification as Classification) ||
    !isNonBlankString(value.message) ||
    !isNonBlankString(value.user_message) ||
    typeof value.retryable !== "boolean"
  ) {
    return false;
  }
  if (
    value.retry_after_ms !== undefined &&
    (typeof value.retry_after_ms !== "number" ||
      !Number.isSafeInteger(value.retry_after_ms) ||
      value.retry_after_ms < 0)
  ) {
    return false;
  }
  const retryAfterRequired =
    value.retryable &&
    (value.classification === "rate_limited" ||
      value.classification === "dependency" ||
      value.classification === "unavailable");
  return !retryAfterRequired || value.retry_after_ms !== undefined;
}

function isIsoUtcTimestamp(value: unknown): value is string {
  if (typeof value !== "string" || !ISO_UTC_TIMESTAMP_RE.test(value)) {
    return false;
  }
  const parsed = Date.parse(value);
  return !Number.isNaN(parsed) && new Date(parsed).toISOString() === value;
}

/** Runtime boundary for the device readiness contract returned by the API. */
export function isKioskConfig(value: unknown): value is KioskConfig {
  return (
    isRecord(value) &&
    isNonBlankString(value.device_id) &&
    isNonBlankString(value.location_label) &&
    typeof value.locale === "string" &&
    KIOSK_LOCALES.has(value.locale as KioskConfig["locale"])
  );
}

function isHttpPunchData(value: unknown): value is HttpPunchData {
  if (!isRecord(value) || !isRecord(value.entry)) return false;
  if (
    value.action !== "clock_in" &&
    value.action !== "clock_out"
  ) {
    return false;
  }
  if (
    !isIsoUtcTimestamp(value.entry.start_at) ||
    (value.entry.end_at !== null && !isIsoUtcTimestamp(value.entry.end_at)) ||
    (value.employee_name !== null && typeof value.employee_name !== "string")
  ) {
    return false;
  }
  if (value.action === "clock_in") return value.entry.end_at === null;
  if (value.entry.end_at === null) return false;
  return Date.parse(value.entry.end_at) >= Date.parse(value.entry.start_at);
}

export interface HttpPunchTransportOptions {
  readonly baseUrl?: string;
  readonly getDeviceToken: () => string | null;
  readonly fetch?: typeof globalThis.fetch;
}

const PRODUCTION_HTTP_OPTIONS: HttpPunchTransportOptions = {
  getDeviceToken: readProvisionedDeviceToken,
};

function resolvedBaseUrl(options: HttpPunchTransportOptions): string {
  return resolveKioskApiBaseUrl({
    configuredBaseUrl:
      options.baseUrl ?? import.meta.env["VITE_PLATFORM_GRAPH_BASE_URL"],
    mode: import.meta.env.PROD ? "production" : "development",
  });
}

function failureFromStatus(
  status: number,
  metadata: KioskPunchErrorMetadata = {},
): KioskPunchError {
  // A 5xx is not proof that the upstream service rolled back: it may have
  // committed the durable punch before a proxy or response path failed. Keep
  // this fact on the transport error so its retry replays the same key/body.
  const resolvedMetadata: KioskPunchErrorMetadata = {
    ...metadata,
    ambiguous: metadata.ambiguous ?? status >= 500,
  };
  if (status === 401) {
    return new KioskPunchError(
      "errors.code.kiosk.device_auth_invalid",
      undefined,
      "authentication",
      resolvedMetadata,
    );
  }
  if (status === 429) {
    return new KioskPunchError(
      "errors.code.kiosk.pin_rate_limited",
      undefined,
      "rate_limited",
      resolvedMetadata,
    );
  }
  return new KioskPunchError(
    "errors.code.platform.unhandled_error",
    undefined,
    "internal",
    resolvedMetadata,
  );
}

function invalidSuccessPayload(ambiguous: boolean): KioskPunchError {
  return new KioskPunchError(
    "errors.code.platform.unhandled_error",
    undefined,
    "internal",
    { ambiguous },
  );
}

function failureFromEnvelope(
  envelope: HttpEnvelope<unknown>,
  status: number,
  metadata: KioskPunchErrorMetadata = {},
): KioskPunchError {
  const entry = Array.isArray(envelope.errors) ? envelope.errors[0] : undefined;
  if (isErrorEnvelopeEntry(entry)) {
    return new KioskPunchError(
      `errors.code.${entry.code}`,
      undefined,
      entry.classification,
      {
        retryable: entry.retryable,
        retryAfterMs: entry.retry_after_ms,
        traceId: envelope.meta?.trace_id,
        ambiguous: metadata.ambiguous ?? status >= 500,
      },
    );
  }
  return failureFromStatus(status, metadata);
}

async function requestData<TData>(
  options: HttpPunchTransportOptions,
  path: "/v1/kiosk/config" | "/v1/kiosk/presence-code" | "/v1/kiosk/punch",
  init: { readonly method: "GET" | "POST"; readonly body?: string; readonly idempotencyKey?: string },
): Promise<TData> {
  const token = options.getDeviceToken();
  if (token === null) {
    throw failureFromStatus(401);
  }

  const headers = new Headers({ Authorization: `KioskDevice ${token}` });
  if (init.body !== undefined) headers.set("Content-Type", "application/json");
  if (init.idempotencyKey !== undefined) {
    headers.set("x-idempotency-key", init.idempotencyKey);
  }
  const request = new Request(`${resolvedBaseUrl(options)}${path}`, {
    method: init.method,
    headers,
    ...(path === "/v1/kiosk/presence-code" && { cache: "no-store" as const }),
    ...(init.body !== undefined && { body: init.body }),
  });

  let response: Response;
  try {
    response = await (options.fetch ?? globalThis.fetch)(request);
  } catch {
    // Never include the request, Authorization header, token, or PIN in errors.
    throw new KioskPunchError(
      "errors.code.platform.network_error",
      undefined,
      "dependency",
      { ambiguous: true },
    );
  }

  let parsedEnvelope: unknown;
  try {
    parsedEnvelope = await response.json();
  } catch {
    // A malformed/empty response may have been injected by a proxy after the
    // server committed. Treat it as ambiguous regardless of its status code.
    throw failureFromStatus(response.status, { ambiguous: true });
  }

  const ambiguousSuccess = path === "/v1/kiosk/punch";
  if (!isRecord(parsedEnvelope)) {
    if (!response.ok) throw failureFromStatus(response.status);
    throw invalidSuccessPayload(ambiguousSuccess);
  }
  const envelope = parsedEnvelope as HttpEnvelope<TData>;

  if (!response.ok) {
    throw failureFromEnvelope(envelope, response.status);
  }
  if (Array.isArray(envelope.errors) && envelope.errors[0] !== undefined) {
    throw failureFromEnvelope(envelope, response.status, {
      ambiguous: ambiguousSuccess,
    });
  }
  if (envelope.errors !== undefined || envelope.data === undefined) {
    throw invalidSuccessPayload(ambiguousSuccess);
  }
  return envelope.data;
}

export async function fetchKioskConfig(
  options: HttpPunchTransportOptions = PRODUCTION_HTTP_OPTIONS,
): Promise<KioskConfig> {
  const data = await requestData<unknown>(options, "/v1/kiosk/config", {
    method: "GET",
  });
  if (!isKioskConfig(data)) throw invalidSuccessPayload(false);
  return data;
}

export async function fetchKioskPresenceCode(
  options: HttpPunchTransportOptions = PRODUCTION_HTTP_OPTIONS,
): Promise<KioskPresenceCode> {
  const data = await requestData<unknown>(options, "/v1/kiosk/presence-code", {
    method: "GET",
  });
  if (
    !isRecord(data) ||
    !isNonBlankString(data.presence_code) ||
    !isNonBlankString(data.device_id) ||
    !isIsoUtcTimestamp(data.expires_at)
  ) {
    throw invalidSuccessPayload(false);
  }
  return {
    presence_code: data.presence_code,
    device_id: data.device_id,
    expires_at: data.expires_at,
  };
}

export function createHttpPunchTransport(
  options: HttpPunchTransportOptions,
): PunchTransport {
  let pendingIntent:
    | { readonly body: string; readonly idempotencyKey: string }
    | undefined;

  return {
    kind: "http",
    cancelPendingIntent(): void {
      pendingIntent = undefined;
    },
    async punch(request: KioskPunchRequest): Promise<KioskPunchResult> {
      const body = JSON.stringify({
        pin: request.pin,
        direction: request.direction,
        timezone: request.timezone,
      });
      const intent =
        pendingIntent?.body === body
          ? pendingIntent
          : { body, idempotencyKey: crypto.randomUUID() };
      pendingIntent = intent;

      try {
        const data = await requestData<unknown>(options, "/v1/kiosk/punch", {
          method: "POST",
          body,
          idempotencyKey: intent.idempotencyKey,
        });
        const expectedAction =
          request.direction === "in" ? "clock_in" : "clock_out";
        if (!isHttpPunchData(data) || data.action !== expectedAction) {
          throw invalidSuccessPayload(true);
        }
        if (pendingIntent === intent) pendingIntent = undefined;
        return {
          action: data.action,
          at:
            data.action === "clock_in"
              ? data.entry.start_at
              : (data.entry.end_at ?? data.entry.start_at),
          employeeName: data.employee_name,
        };
      } catch (error) {
        if (!(error instanceof KioskPunchError && error.ambiguous) && pendingIntent === intent) {
          pendingIntent = undefined;
        }
        throw error;
      }
    },
  };
}

/** Production default. Tests/dev previews inject createMockPunchTransport explicitly. */
export const punchTransport: PunchTransport = createHttpPunchTransport(
  PRODUCTION_HTTP_OPTIONS,
);
